import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppSelector } from '@app/hooks';
import type { KnowledgeDocument } from '@entities/knowledge/types';
import { useAgentPermission } from '@entities/user';
import {
  useArchiveDocumentMutation,
  useListKnowledgeQuery,
  useToggleDocumentMutation,
} from '@shared/api';
import { downloadAuthorizedFile } from '@shared/lib/downloadFile';
import { ChecklistRulesModal } from '../ChecklistRulesModal';
import { ContractTemplates } from '../ContractTemplates';
import { DocumentChunksModal } from '../DocumentChunksModal';
import { SOURCE_TABS, SOURCE_TYPES } from './constants';
import { KnowledgeBaseTable } from './KnowledgeBaseTable';
import { OutgoingTemplatesView } from './OutgoingTemplatesView';
import { SourceTab, SourceTabs } from './styles';
import type { Source } from './types';
import { UploadModal } from './UploadModal';

export function KnowledgeBase() {
  const [params, setParams] = useSearchParams();
  const source = (params.get('source') ?? '') as Source;
  const [filterType, setFilterType] = useState<string>('');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [detailsDoc, setDetailsDoc] = useState<KnowledgeDocument | null>(null);
  const [archive] = useArchiveDocumentMutation();
  const [toggleDoc] = useToggleDocumentMutation();
  const accessToken = useAppSelector((s) => s.auth.accessToken);
  const { can_write } = useAgentPermission('statut');

  async function downloadTemplateBlob(docId: string, title: string) {
    const base = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';
    const res = await downloadAuthorizedFile(
      `${base}/agents/statut/knowledge/${docId}/template-blob`,
      { accessToken, fallbackName: `${title}.docx` },
    );
    if (!res.ok) alert(`Не удалось скачать оригинал шаблона (${res.error}).`);
  }

  const setSource = (s: Source) => {
    const next = new URLSearchParams(params);
    if (s) next.set('source', s);
    else next.delete('source');
    setParams(next, { replace: true });
  };

  // Эффективный фильтр: либо точный document_type из локального селекта,
  // либо одиночный тип в группе source-табов (для группы 'internal').
  const effectiveDocType: string | undefined = (() => {
    if (filterType) return filterType;
    if (source && source !== 'template') {
      const list = SOURCE_TYPES[source];
      if (list.length === 1) return list[0];
    }
    return undefined;
  })();

  // Poll only while there is something to wait for.
  const baseQuery = useListKnowledgeQuery(
    effectiveDocType ? { document_type: effectiveDocType } : undefined,
  );
  const hasProcessing = !!baseQuery.data?.items.some((d) => d.status === 'processing');
  const { data, isLoading } = useListKnowledgeQuery(
    effectiveDocType ? { document_type: effectiveDocType } : undefined,
    { pollingInterval: hasProcessing ? 3000 : 0 },
  );

  // Если выбрана группа из нескольких document_type — фильтруем результат
  // локально (backend отдал всё, что не contract_template).
  const filteredItems = (() => {
    const all = data?.items ?? [];
    if (source === 'tech' || source === 'legal' || source === 'response') {
      const allowed = new Set(SOURCE_TYPES[source]);
      return all.filter((d) => allowed.has(d.document_type));
    }
    return all;
  })();

  // Если выбраны Типовые формы — отдаём управление автономному компоненту.
  if (source === 'template') {
    return (
      <div>
        <SourceTabs>
          {SOURCE_TABS.map((t) => (
            <SourceTab
              key={t.value}
              $active={source === t.value}
              type="button"
              onClick={() => setSource(t.value)}
            >
              {t.label}
            </SourceTab>
          ))}
        </SourceTabs>
        <ContractTemplates />
      </div>
    );
  }

  return (
    <div>
      <SourceTabs>
        {SOURCE_TABS.map((t) => (
          <SourceTab
            key={t.value}
            $active={source === t.value}
            type="button"
            onClick={() => {
              setSource(t.value);
              setFilterType(''); // сбрасываем точечный фильтр при смене группы
            }}
          >
            {t.label}
          </SourceTab>
        ))}
      </SourceTabs>

      {source === 'response' ? (
        <OutgoingTemplatesView
          items={filteredItems}
          isLoading={isLoading}
          canWrite={can_write}
          onUpload={() => setUploadOpen(true)}
          onDownload={downloadTemplateBlob}
          onArchive={(id) => archive(id)}
          onDetails={setDetailsDoc}
          onToggle={(id) => toggleDoc(id)}
        />
      ) : (
        <KnowledgeBaseTable
          items={filteredItems}
          isLoading={isLoading}
          canWrite={can_write}
          filterType={filterType}
          source={source}
          onFilterChange={setFilterType}
          onUploadClick={() => setUploadOpen(true)}
          onDetailsClick={setDetailsDoc}
          onDownloadClick={downloadTemplateBlob}
          onArchiveClick={(id) => archive(id)}
        />
      )}

      <UploadModal
        key={source}
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        defaultDocType={source === 'rules' ? 'checklist' : undefined}
      />
      {source === 'rules' ? (
        <ChecklistRulesModal
          open={!!detailsDoc}
          document={detailsDoc}
          onClose={() => setDetailsDoc(null)}
        />
      ) : (
        <DocumentChunksModal
          open={!!detailsDoc}
          document={detailsDoc}
          onClose={() => setDetailsDoc(null)}
        />
      )}
    </div>
  );
}
